import React, { useState, useMemo } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Checkbox } from './ui/checkbox';
import { ChevronDown, ChevronRight, X, Factory } from 'lucide-react';
import {
  STANDARD_INDUSTRIES,
  INDUSTRY_CATEGORIES,
  PROJECT_SECTOR_GROUPS,
  PROJECT_SECTOR_IDS,
  getIndustryLabel,
} from '../constants/industries';

/**
 * Industry filter grouped by main category.
 * - Each category row has a tri-state checkbox (none / some / all selected).
 * - Clicking the category checkbox toggles select-all / deselect-all for that group.
 * - Clicking the category row header toggles expand/collapse.
 * - Individual industries can still be toggled one by one.
 * WO-73:
 * - scope="projects" shows the project sectors (grouped by what is being built)
 *   instead of the full industry list.
 * - max={3} (project forms): at most 3, the first one chosen is the MAIN sector
 *   and is marked as such; no "select all" / group ticks in that mode.
 */
const IndustryMultiSelectFilter = ({
  label,
  icon: Icon = Factory,
  selected = [],
  onChange,
  language = 'en',
  isRTL = false,
  testId,
  triggerClassName = '',
  popoverAlign = 'end',
  scope = 'all',
  max = null,
}) => {
  const [open, setOpen] = useState(false);
  const isProjects = scope === 'projects';
  const limited = typeof max === 'number' && max > 0;

  // Build categories -> industries map once
  const groups = useMemo(() => {
    if (isProjects) {
      return PROJECT_SECTOR_GROUPS.map((g) => {
        const items = g.sectors.map((id) => ({ value: id, label: getIndustryLabel(id, language) }));
        return {
          id: g.id,
          label: language === 'ar' ? g.ar : g.en,
          items,
          valueSet: new Set(items.map((i) => i.value)),
        };
      });
    }
    return INDUSTRY_CATEGORIES.map((cat) => {
      const items = STANDARD_INDUSTRIES.filter((ind) => ind.category === cat.id).map((ind) => ({
        value: ind.id,
        label: language === 'ar' ? ind.ar : ind.en,
      }));
      return {
        id: cat.id,
        label: language === 'ar' ? cat.ar : cat.en,
        items,
        valueSet: new Set(items.map((i) => i.value)),
      };
    }).filter((g) => g.items.length > 0);
  }, [language, isProjects]);

  // Default expanded state: all categories expanded so users see the hierarchy
  const [expanded, setExpanded] = useState(() => {
    const map = {};
    (isProjects ? PROJECT_SECTOR_GROUPS : INDUSTRY_CATEGORIES).forEach((c) => {
      map[c.id] = true;
    });
    return map;
  });

  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const toggleIndustry = (value) => {
    if (selectedSet.has(value)) {
      onChange(selected.filter((v) => v !== value));
    } else if (limited && selected.length >= max) {
      // full: nothing added (the option is shown greyed out)
    } else {
      onChange([...selected, value]);
    }
  };

  const toggleCategory = (group) => {
    const allSelected = group.items.every((i) => selectedSet.has(i.value));
    if (allSelected) {
      // Remove all industries of this category
      onChange(selected.filter((v) => !group.valueSet.has(v)));
    } else {
      // Add all missing industries of this category
      const next = new Set(selected);
      group.items.forEach((i) => next.add(i.value));
      onChange(Array.from(next));
    }
  };

  const toggleExpanded = (catId) => {
    setExpanded((prev) => ({ ...prev, [catId]: !prev[catId] }));
  };

  const clearAll = () => onChange([]);
  const selectAll = () => onChange(isProjects ? [...PROJECT_SECTOR_IDS] : STANDARD_INDUSTRIES.map((i) => i.id));
  const mainLabel = language === 'ar' ? 'رئيسي' : 'Main';

  // Category check state: 'none' | 'some' | 'all'
  const categoryState = (group) => {
    const total = group.items.length;
    const count = group.items.filter((i) => selectedSet.has(i.value)).length;
    if (count === 0) return { state: 'none', count };
    if (count === total) return { state: 'all', count };
    return { state: 'some', count };
  };

  const triggerText = () => {
    if (selected.length === 0) return label || (language === 'ar' ? 'اختر الصناعة...' : 'Select industry...');
    if (selected.length === 1) {
      return getIndustryLabel(selected[0], language) || selected[0];
    }
    return `${selected.length} ${language === 'ar' ? 'محدد' : 'selected'}`;
  };

  const ChevronSide = isRTL ? ChevronRight : ChevronDown;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={`h-10 px-3 justify-between min-w-[160px] ${triggerClassName}`}
          data-testid={testId}
        >
          <div className="flex items-center gap-2">
            <Icon className="w-4 h-4 text-muted-foreground" />
            <span className="truncate max-w-[120px]">{triggerText()}</span>
          </div>
          <ChevronSide className="w-4 h-4 text-muted-foreground ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[360px] max-w-[calc(100vw-16px)] p-0" align={popoverAlign} avoidCollisions={true}>
        <div className="p-2 border-b flex items-center justify-between">
          {limited ? (
            <span className="text-xs text-muted-foreground px-2">
              {language === 'ar'
                ? `حتى ${max}، الأولى هي الصناعة الرئيسية`
                : `Up to ${max} — the first one is the main industry`}
            </span>
          ) : (
          <Button
            variant="ghost"
            size="sm"
            onClick={selectAll}
            className="h-7 text-xs"
            data-testid={testId ? `${testId}-select-all` : undefined}
          >
            {language === 'ar' ? 'تحديد الكل' : 'Select All'}
          </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={clearAll}
            className="h-7 text-xs"
            data-testid={testId ? `${testId}-clear` : undefined}
          >
            {language === 'ar' ? 'مسح' : 'Clear'}
          </Button>
        </div>
        <ScrollArea className="h-[320px]">
          <div className="p-2 space-y-1">
            {groups.map((group) => {
              const { state, count } = categoryState(group);
              const isExpanded = !!expanded[group.id];
              const CatChevron = isExpanded ? ChevronDown : (isRTL ? ChevronRight : ChevronRight);
              return (
                <div key={group.id} className="space-y-1">
                  {/* Category header row */}
                  <div
                    className="flex items-center gap-2 px-2 py-2 rounded-md hover:bg-muted/70 transition-colors cursor-pointer bg-muted/40"
                    data-testid={testId ? `${testId}-category-${group.id.replace(/\s+/g, '-').toLowerCase()}` : undefined}
                  >
                    {!limited && (
                    <Checkbox
                      checked={state === 'all' ? true : state === 'some' ? 'indeterminate' : false}
                      onCheckedChange={() => toggleCategory(group)}
                      onClick={(e) => e.stopPropagation()}
                      data-testid={testId ? `${testId}-category-check-${group.id.replace(/\s+/g, '-').toLowerCase()}` : undefined}
                    />
                    )}
                    <button
                      type="button"
                      onClick={() => toggleExpanded(group.id)}
                      className="flex items-center gap-1 flex-1 text-left"
                    >
                      <CatChevron className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                      <span className="text-sm font-semibold flex-1 min-w-0 truncate">{group.label}</span>
                    </button>
                  </div>

                  {/* Industries under this category */}
                  {isExpanded && (
                    <div className={isRTL ? 'pr-4 space-y-0.5' : 'pl-4 space-y-0.5'}>
                      {group.items.map((option) => {
                        const isOn = selectedSet.has(option.value);
                        const isFull = limited && !isOn && selected.length >= max;
                        return (
                        <div
                          key={option.value}
                          className={`flex items-center gap-2 px-2 py-1.5 rounded-md transition-colors ${
                            isFull ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer hover:bg-muted'
                          } ${isOn ? 'bg-primary/10' : ''}`}
                          onClick={() => toggleIndustry(option.value)}
                          data-testid={testId ? `${testId}-option-${option.value}` : undefined}
                        >
                          <Checkbox
                            checked={isOn}
                            disabled={isFull}
                            onCheckedChange={() => toggleIndustry(option.value)}
                            className="pointer-events-none"
                          />
                          <span className="text-sm flex-1 truncate">{option.label}</span>
                          {limited && selected[0] === option.value && (
                            <Badge variant="outline" className="text-[10px] px-1 py-0">{mainLabel}</Badge>
                          )}
                        </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </ScrollArea>
        {selected.length > 0 && (
          <div className="p-2 border-t">
            <div className="flex flex-wrap gap-1">
              {selected.slice(0, 3).map((val, i) => (
                <Badge key={val} variant={limited && i === 0 ? 'default' : 'secondary'} className="text-xs">
                  {(getIndustryLabel(val, language) || val).slice(0, limited ? 28 : 14)}
                  <X
                    className="w-3 h-3 ml-1 cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleIndustry(val);
                    }}
                  />
                </Badge>
              ))}
              {selected.length > 3 && (
                <Badge variant="outline" className="text-xs">
                  +{selected.length - 3}
                </Badge>
              )}
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default IndustryMultiSelectFilter;
